/** Content-fit layout for Generate Image success results (P1).
 *
 * Success previews use available card width and intrinsic aspect ratio —
 * never a fixed 16:9 / min-height stage. Empty/busy placeholders keep
 * stable geometry via separate helpers. */

export const IMAGE_RESULT_AREA_MARGIN_X_PX = 24
export const IMAGE_SUCCESS_RESULT_INSET_X_PX = 8
export const IMAGE_SUCCESS_RESULT_INSET_BOTTOM_PX = 8
export const IMAGE_RESULT_SECTION_LABEL_HEIGHT_PX = 28

/** Expanded success preview — tall portraits may grow the node but stay bounded. */
export const IMAGE_SUCCESS_RESULT_MAX_HEIGHT_EXPANDED = 640

/** Collapsed success preview — slightly taller than legacy 200px cap. */
export const IMAGE_SUCCESS_RESULT_MAX_HEIGHT_COLLAPSED = 360

export const IMAGE_BUSY_PLACEHOLDER_MIN_HEIGHT_EXPANDED = 160
export const IMAGE_BUSY_PLACEHOLDER_MIN_HEIGHT_COLLAPSED = 120

export interface ImageSuccessDisplaySize {
  width: number
  height: number
}

export function imageSuccessContentWidth(cardWidthPx: number, expanded: boolean): number {
  const marginX = expanded ? IMAGE_RESULT_AREA_MARGIN_X_PX : 0
  const insetX = IMAGE_SUCCESS_RESULT_INSET_X_PX * 2
  return Math.max(120, cardWidthPx - marginX - insetX)
}

/** Scale image to use available width first; clamp height without cropping. */
export function computeImageSuccessDisplaySize(
  naturalWidth: number,
  naturalHeight: number,
  availableWidth: number,
  maxHeight: number,
): ImageSuccessDisplaySize {
  if (!naturalWidth || !naturalHeight || !availableWidth || !maxHeight) {
    return {
      width: availableWidth,
      height: Math.min(maxHeight, IMAGE_BUSY_PLACEHOLDER_MIN_HEIGHT_EXPANDED),
    }
  }

  let width = availableWidth
  let height = width * (naturalHeight / naturalWidth)
  if (height > maxHeight) {
    const scale = maxHeight / height
    width = Math.round(width * scale)
    height = maxHeight
  } else {
    width = Math.round(width)
    height = Math.round(height)
  }
  return { width, height }
}

export function imageSuccessResultAreaHeight(
  imageSize: ImageSuccessDisplaySize,
  expanded: boolean,
): number {
  const label = expanded ? IMAGE_RESULT_SECTION_LABEL_HEIGHT_PX : 0
  const bottomInset = expanded ? IMAGE_SUCCESS_RESULT_INSET_BOTTOM_PX : 4
  return label + imageSize.height + bottomInset
}
